"use client";

import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaWhatsapp,
  FaPhoneAlt,
  FaEnvelope,
} from "react-icons/fa";

export default function Footer() {
  return (
    // ✅ SEO: <footer> is a semantic landmark — Google uses it for site structure
    <motion.footer
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true }}
      aria-label="Fiieve site footer"
      className="text-white border-t border-green-400/20"
      style={{ backgroundColor: "#00321a", backgroundImage: "none" }}
      // ✅ SEO: schema.org Organization microdata — Google reads this for Knowledge Panel
      itemScope
      itemType="https://schema.org/Organization"
    >
      {/* ── MAIN CONTENT — pagedone pattern: flex row, brand left, nav grid right ── */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col xl:flex-row gap-8 pt-10 pb-10">

          {/* ── BRAND — fixed width, left side ── */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            viewport={{ once: true }}
            className="w-full xl:max-w-[260px] xl:flex-shrink-0 flex flex-col gap-5"
            itemScope itemType="https://schema.org/Organization"
          >
            <Link to="/" aria-label="Fiieve — Software & Engineering Company — Home">
              <img
                src="/Images/logo.png"
                alt="Fiieve — Web Development & AI Automation Company UK"
                className="h-20 w-auto object-contain"
                loading="lazy"
                width={140}
                height={80}
                itemProp="logo"
              />
            </Link>

            <p className="text-white/60 text-sm leading-relaxed" itemProp="description">
              Fiieve is a UK-based software and engineering company delivering web development, mobile apps, AI automation, IoT, and embedded systems solutions for startups and enterprises.
            </p>

            <div className="flex gap-3" aria-label="Fiieve social media links">
              {[
                { icon: <FaFacebookF />,  href: "https://facebook.com",                color: "hover:text-blue-500 hover:border-blue-500",   label: "Fiieve on Facebook" },
                { icon: <FaInstagram />,  href: "https://instagram.com",               color: "hover:text-pink-500 hover:border-pink-500",   label: "Fiieve on Instagram" },
                { icon: <FaLinkedinIn />, href: "https://linkedin.com/company/fiieve", color: "hover:text-blue-400 hover:border-blue-400",   label: "Fiieve on LinkedIn" },
                { icon: <FaWhatsapp />,   href: "https://wa.me/447466761773",          color: "hover:text-green-400 hover:border-green-400", label: "Chat with Fiieve on WhatsApp" },
              ].map((s, i) => (
                <a
                  key={i}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className={`w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/60 text-sm transition-all duration-300 ${s.color}`}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </motion.div>

          {/* ── 5 NAV COLUMNS — same inner grid as pagedone ── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-8 w-full">

            {/* MENU */}
            <motion.nav
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.6 }}
              viewport={{ once: true }}
              className="w-full text-left"
              aria-label="Footer navigation"
            >
              <h2 className="text-sm uppercase font-bold text-green-400 mb-5 tracking-widest">Menu</h2>
              <ul className="space-y-3">
                {[
                  { name: "Home",        path: "/" },
                  { name: "About Us",   path: "/about-us" },
                  { name: "Services",   path: "/services" },
                  { name: "Portfolio",  path: "/portfolio" },
                  { name: "Careers",    path: "/careers" },
                  { name: "Contact Us", path: "/contact-us" },
                ].map((item) => (
                  <li key={item.name}>
                    <Link to={item.path} className="text-white/60 hover:text-green-400 transition-colors duration-300 text-sm">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.nav>

            {/* SOFTWARE SERVICES */}
            <motion.nav
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              viewport={{ once: true }}
              className="w-full text-left"
              aria-label="Fiieve software services navigation"
            >
              <h2 className="text-sm uppercase font-bold text-green-400 mb-5 tracking-widest">Software</h2>
              <ul className="space-y-3">
                {[
                  { name: "Mobile App Development",      path: "/mobile-app-development" },
                  { name: "Web Development",             path: "/web-development" },
                  { name: "Graphic & UI/UX Design",      path: "/ui-ux-design" },
                  { name: "Custom Software Development", path: "/custom-software-development" },
                  { name: "AI Automation",               path: "/ai-automation" },
                ].map((item) => (
                  <li key={item.name}>
                    <Link to={item.path} className="text-white/60 hover:text-green-400 transition-colors duration-300 text-sm">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.nav>

            {/* ENGINEERING SERVICES */}
            <motion.nav
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.6 }}
              viewport={{ once: true }}
              className="w-full text-left"
              aria-label="Fiieve engineering services navigation"
            >
              <h2 className="text-sm uppercase font-bold text-green-400 mb-5 tracking-widest">Engineering</h2>
              <ul className="space-y-3">
                {[
                  { name: "IoT Development",       path: "/engineering/iot" },
                  { name: "Embedded Systems",      path: "/engineering/embedded-systems" },
                  { name: "Robotics & Automation", path: "/engineering/robotics" },
                  { name: "PCB Design",            path: "/engineering/pcb-design" },
                  { name: "Smart Home Systems",    path: "/engineering/smart-home" },
                ].map((item) => (
                  <li key={item.name}>
                    <Link to={item.path} className="text-white/60 hover:text-green-400 transition-colors duration-300 text-sm">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.nav>

            {/* OUR OFFICES */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              viewport={{ once: true }}
              className="w-full text-left"
            >
              <h2 className="text-sm uppercase font-bold text-green-400 mb-5 tracking-widest">Our Offices</h2>
              <ul className="space-y-4 text-sm text-white/60">
                <li className="flex gap-2 items-start" itemScope itemType="https://schema.org/PostalAddress">
                  <img src="/Images/UK.png" alt="UK flag" className="w-4 h-4 mt-0.5 flex-shrink-0 object-contain" loading="lazy" />
                  <span><strong className="text-white">UK</strong><br />15 Kingston Road,<br />Ilford IG1 1PA, London</span>
                </li>
                <li className="flex gap-2 items-start">
                  <img src="/Images/qatar.png" alt="Qatar flag" className="w-4 h-4 mt-0.5 flex-shrink-0 object-contain" loading="lazy" />
                  <span><strong className="text-white">Qatar</strong><br />Al Sadd Center, Doha</span>
                </li>
                <li className="flex gap-2 items-start">
                  <img src="/Images/pakistan.png" alt="Pakistan flag" className="w-4 h-4 mt-0.5 flex-shrink-0 object-contain" loading="lazy" />
                  <span><strong className="text-white">Pakistan</strong><br />Blue Area, Islamabad</span>
                </li>
              </ul>
            </motion.div>

            {/* CONTACT */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.6 }}
              viewport={{ once: true }}
              className="w-full text-left"
            >
              <h2 className="text-sm uppercase font-bold text-green-400 mb-5 tracking-widest">Contact Us</h2>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-sm">
                  <FaEnvelope className="text-green-400 flex-shrink-0" aria-hidden="true" />
                  <a href="mailto:info@fiieve.com" aria-label="Email Fiieve" className="text-white/60 hover:text-green-400 transition-colors" itemProp="email">
                    info@fiieve.com
                  </a>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <FaPhoneAlt className="text-green-400 flex-shrink-0" aria-hidden="true" />
                  <a href="tel:+447843735258" aria-label="Call Fiieve" className="text-white/60 hover:text-green-400 transition-colors" itemProp="telephone">
                    +44 7843 735258
                  </a>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <FaWhatsapp className="text-green-400 flex-shrink-0" aria-hidden="true" />
                  <a href="https://wa.me/447843735258" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp Fiieve" className="text-white/60 hover:text-green-400 transition-colors">
                    Chat on WhatsApp
                  </a>
                </li>
              </ul>
              <p className="text-white/40 text-xs mt-4">
                We respond within 24 hours.
              </p>
            </motion.div>

          </div>{/* end 5-col grid */}
        </div>{/* end flex row */}
      </div>{/* end max-w wrapper */}

      {/* ── BOTTOM BAR ── */}
      <div className="border-t border-green-400/10 py-5 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40">
          {/* ✅ SEO: copyright + company name visible in footer */}
          <p itemProp="name">
            © {new Date().getFullYear()} Fiieve — Software & Engineering Company. All Rights Reserved.
          </p>

          <div className="flex gap-5">
            {/* ✅ SEO: correct paths matching App.js */}
            <Link to="/privacy-policy" className="hover:text-green-400 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="hover:text-green-400 transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </motion.footer>
  );
}